import { NavigateFunction } from 'react-router-dom';

import {
  DASHBOARD_ROUTE_ROOT,
  DASHBOARD_ROUTES,
  DELIVERY_NOTE_ROUTE_ROOT,
  DELIVERY_NOTE_ROUTES,
  HOME_ROUTE_ROOT,
  HOME_ROUTES,
  INBOUND_ROUTE_ROOT,
  INBOUND_ROUTES,
  INVENTORY_ADJUSTMENT_ROOT,
  INVENTORY_ADJUSTMENT_ROUTES,
  PACK_TASK_ROUTE_ROOT,
  PACK_TASK_ROUTES,
  PICK_TASK_ROUTE_ROOT,
  PICK_TASK_ROUTES,
  REPLENISH_ROUTE_ROOT,
  REPLENISH_ROUTES,
  STO_CHECK_ROUTE_ROOT,
  STO_CHECK_ROUTES,
  STO_PICK_ROUTE_ROOT,
  STO_PICK_ROUTES,
  STO_RECEIVE_ROUTE_ROOT,
  STO_RECEIVE_ROUTES,
  STOCK_TRANSFER_ROUTE_ROOT,
  STOCK_TRANSFER_ROUTES,
} from 'routes/constants';

import { IHeaderConfig } from '../interfaces';

const _getDashboardHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case DASHBOARD_ROUTES.SUMMARY:
    case DASHBOARD_ROUTES.ORDER_ITEM:
    case DASHBOARD_ROUTES.USERS:
    case DASHBOARD_ROUTES.REPLENISH_ITEM:
    case DASHBOARD_ROUTES.STOCK_TRANSFER_ITEM:
    case DASHBOARD_ROUTES.PRODUCT_UPLOAD:
    case DASHBOARD_ROUTES.PHARMACY_UPLOAD:
    case DASHBOARD_ROUTES.USER_UPLOAD:
    case DASHBOARD_ROUTES.COMPANY_INFO:
    case DASHBOARD_ROUTES.BINS:
    case DASHBOARD_ROUTES.INVENTORY_ADJUSTMENT:
    case DASHBOARD_ROUTES.STO:
    case DASHBOARD_ROUTES.INBOUND:
    case DASHBOARD_ROUTES.DELIVERY_NOTE:
      return { title: 'Dashboard', prevPageHandler: () => navigate('/') };
    default:
      return { title: null, prevPageHandler: null };
  }
};

export const _getHomeHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case HOME_ROUTES.HOME_PAGE:
      return { title: null, prevPageHandler: null };
    case HOME_ROUTES.OUTBOUND:
      return {
        title: 'Outbound',
        prevPageHandler: (): void => {
          navigate('/');
        },
      };
    case HOME_ROUTES.STO:
      return {
        title: 'STO',
        prevPageHandler: (): void => {
          navigate('/');
        },
      };
    case HOME_ROUTES.INBOUND:
      return {
        title: 'Inbound',
        prevPageHandler: (): void => {
          navigate('/');
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

export const _getInventoryHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case INVENTORY_ADJUSTMENT_ROUTES.LIST_PAGE:
      return {
        title: 'List Page',
        prevPageHandler: (): void => {
          navigate('/');
        },
      };
    case INVENTORY_ADJUSTMENT_ROUTES.DETAILS:
      return {
        title: 'Details',
        prevPageHandler: (): void => {
          navigate(INVENTORY_ADJUSTMENT_ROUTES.LIST_PAGE);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getPickAndPackHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
  navigateParams?: Record<string, any>,
): IHeaderConfig => {
  switch (source) {
    case PACK_TASK_ROUTES.PACK_TASK:
      return { title: 'Pack Items', prevPageHandler: null };
    case PACK_TASK_ROUTES.PRINT:
      return {
        title: 'Packing',
        prevPageHandler: navigateParams?.poNumber
          ? () =>
              navigate({
                pathname: PACK_TASK_ROUTES.CHECK_BASKET,
                search: `poNumber=${navigateParams.poNumber}`,
              })
          : null,
      };
    case PACK_TASK_ROUTES.SUMMARY:
      return {
        title: 'Packing Summary',
        prevPageHandler: navigateParams?.poNumber
          ? () =>
              navigate({
                pathname: PACK_TASK_ROUTES.PRINT,
                search: `poNumber=${navigateParams.poNumber}`,
              })
          : null,
      };
    case PACK_TASK_ROUTES.ALLOCATION_STAGING:
      return {
        title: 'Allocation Staging',
        prevPageHandler: navigateParams?.poNumber
          ? () =>
              navigate({
                pathname: PACK_TASK_ROUTES.PRINT,
                search: `poNumber=${navigateParams.poNumber}`,
              })
          : null,
      };
    case PACK_TASK_ROUTES.CHECK_BASKET:
      return {
        title: 'Check Baskets',
        prevPageHandler: () => navigate(HOME_ROUTES.OUTBOUND),
      };
    case PICK_TASK_ROUTES.ASSIGN:
      return {
        title: 'Picking',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.OUTBOUND);
        },
      };
    case PICK_TASK_ROUTES.LIST:
      return {
        title: 'Picking',
        prevPageHandler: () => {
          navigate(PICK_TASK_ROUTES.ASSIGN);
        },
      };
    case PICK_TASK_ROUTES.ITEM:
      return {
        title: 'Pick Task',
        prevPageHandler: () => {
          navigate(PICK_TASK_ROUTES.LIST);
        },
      };
    case PICK_TASK_ROUTES.SUMMARY:
      return {
        title: 'Pick Summary',
        prevPageHandler: () => {
          navigate(PICK_TASK_ROUTES.LIST);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getReplenishHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  // handle case url contains params
  let searchSource = source;
  if (source.includes(REPLENISH_ROUTES.TASK_SOURCE.replace('/:skuId', ''))) {
    searchSource = REPLENISH_ROUTES.TASK_SOURCE;
  } else if (
    source.includes(REPLENISH_ROUTES.TASK_DESTINATION.replace('/:skuId', ''))
  ) {
    searchSource = REPLENISH_ROUTES.TASK_DESTINATION;
  }

  switch (searchSource) {
    case REPLENISH_ROUTES.REPLENISH:
      return { title: null, prevPageHandler: null };
    case REPLENISH_ROUTES.ASSIGN:
      return {
        title: 'Replenish',
        prevPageHandler: (): void => {
          navigate(HOME_ROUTES.INBOUND);
        },
      };
    case REPLENISH_ROUTES.LIST_SOURCE:
      return {
        title: 'Replenish List',
        prevPageHandler: () => navigate(REPLENISH_ROUTES.ASSIGN),
      };
    case REPLENISH_ROUTES.LIST_DESTINATION:
      return {
        title: 'Replenish List',
        prevPageHandler: () => navigate(REPLENISH_ROUTES.LIST_SOURCE),
      };
    case REPLENISH_ROUTES.TASK_SOURCE:
      return {
        title: 'Replenish Task | Source',
        prevPageHandler: (): void => {
          navigate(REPLENISH_ROUTES.LIST_SOURCE);
        },
      };
    case REPLENISH_ROUTES.TASK_DESTINATION:
      return {
        title: 'Replenish Task | Destination',
        prevPageHandler: (): void => {
          navigate(REPLENISH_ROUTES.LIST_DESTINATION);
        },
      };
    case REPLENISH_ROUTES.SUMMARY:
      return {
        title: 'Replenish Summary',
        prevPageHandler: () => {
          navigate(REPLENISH_ROUTES.LIST_DESTINATION);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getStockTransferHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  // handle case url contains params
  let searchSource = source;
  if (
    source.includes(STOCK_TRANSFER_ROUTES.TASK_SOURCE.replace('/:skuId', ''))
  ) {
    searchSource = STOCK_TRANSFER_ROUTES.TASK_SOURCE;
  } else if (
    source.includes(
      STOCK_TRANSFER_ROUTES.TASK_DESTINATION.replace('/:skuId', ''),
    )
  ) {
    searchSource = STOCK_TRANSFER_ROUTES.TASK_DESTINATION;
  }

  switch (searchSource) {
    case STOCK_TRANSFER_ROUTES.LIST_SOURCE:
      return {
        title: 'Bin Transfer List',
        prevPageHandler: (): void => {
          navigate(HOME_ROUTES.INBOUND);
        },
      };
    case STOCK_TRANSFER_ROUTES.LIST_DESTINATION:
      return {
        title: 'Bin Transfer List',
        prevPageHandler: (): void => {
          navigate(STOCK_TRANSFER_ROUTES.LIST_SOURCE);
        },
      };
    case STOCK_TRANSFER_ROUTES.TASK_SOURCE:
      return {
        title: 'Bin Transfer Task | Source',
        prevPageHandler: (): void => {
          navigate(STOCK_TRANSFER_ROUTES.LIST_SOURCE);
        },
      };
    case STOCK_TRANSFER_ROUTES.TASK_DESTINATION:
      return {
        title: 'Bin Transfer Task | Destination',
        prevPageHandler: (): void => {
          navigate(STOCK_TRANSFER_ROUTES.LIST_DESTINATION);
        },
      };
    case STOCK_TRANSFER_ROUTES.SUMMARY:
      return {
        title: 'Bin Transfer Summary',
        prevPageHandler: (): void => {
          navigate(STOCK_TRANSFER_ROUTES.LIST_DESTINATION);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getSTOPickHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  // handle case url contains params
  let searchSource = source;
  if (source.includes(STO_PICK_ROUTES.ITEM.replace('/:skuId', ''))) {
    searchSource = STO_PICK_ROUTES.ITEM;
  }

  switch (searchSource) {
    case STO_PICK_ROUTES.ASSIGN:
      return {
        title: 'STO Pick Assign',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.STO);
        },
      };
    case STO_PICK_ROUTES.LIST:
      return {
        title: 'STO Pick List',
        prevPageHandler: () => {
          navigate(STO_PICK_ROUTES.ASSIGN);
        },
      };
    case STO_PICK_ROUTES.ITEM:
      return {
        title: 'STO Pick Item',
        prevPageHandler: () => {
          navigate(STO_PICK_ROUTES.LIST);
        },
      };
    case STO_PICK_ROUTES.SUMMARY:
      return {
        title: 'STO Pick Summary',
        prevPageHandler: () => {
          navigate(STO_PICK_ROUTES.LIST);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getSTOCheckBasketHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case STO_CHECK_ROUTES.CHECK_BASKET:
      return {
        title: 'STO Check Basket',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.STO);
        },
      };
    case STO_CHECK_ROUTES.CHECK_SUMMARY:
      return {
        title: 'STO Check Basket Summary',
        prevPageHandler: () => {
          navigate(STO_CHECK_ROUTES.CHECK_BASKET);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getSTOReceiveHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  // handle case url contains params
  let searchSource = source;
  if (source.includes(STO_RECEIVE_ROUTES.ITEM.replace('/:skuId', ''))) {
    searchSource = STO_RECEIVE_ROUTES.ITEM;
  }

  switch (searchSource) {
    case STO_RECEIVE_ROUTES.ASSIGN:
      return {
        title: 'STO Receive Assign',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.STO);
        },
      };
    case STO_RECEIVE_ROUTES.LIST:
      return {
        title: 'STO Receive List',
        prevPageHandler: () => {
          navigate(STO_RECEIVE_ROUTES.ASSIGN);
        },
      };
    case STO_RECEIVE_ROUTES.ITEM:
      return {
        title: 'STO Receive Item',
        prevPageHandler: () => {
          navigate(STO_RECEIVE_ROUTES.LIST);
        },
      };
    case STO_RECEIVE_ROUTES.SUMMARY:
      return {
        title: 'STO Receive Summary',
        prevPageHandler: () => {
          navigate(STO_RECEIVE_ROUTES.LIST);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getDeliveryNoteHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case DELIVERY_NOTE_ROUTES.LIST:
      return {
        title: 'Delivery Note List',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.OUTBOUND);
        },
      };
    case DELIVERY_NOTE_ROUTES.DETAIL:
      return {
        title: 'Delivery Note Detail',
        prevPageHandler: () => {
          navigate(DELIVERY_NOTE_ROUTES.LIST);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

const _getInboundHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
): IHeaderConfig => {
  switch (source) {
    case INBOUND_ROUTES.ASSIGN:
      return {
        title: 'Inbound Task',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.INBOUND);
        },
      };
    case INBOUND_ROUTES.LIST:
      return {
        title: 'Inbound Task List',
        prevPageHandler: () => {
          navigate(INBOUND_ROUTES.ASSIGN);
        },
      };
    case INBOUND_ROUTES.ITEM:
      return {
        title: 'Inbound Receive Task',
        prevPageHandler: () => {
          navigate(INBOUND_ROUTES.LIST);
        },
      };
    case INBOUND_ROUTES.SUMMARY:
      return {
        title: 'Inbound Task Summary',
        prevPageHandler: () => {
          navigate(INBOUND_ROUTES.LIST);
        },
      };
    case INBOUND_ROUTES.APPROVAL_LIST:
      return {
        title: 'Inbound Approval List',
        prevPageHandler: () => {
          navigate(HOME_ROUTES.INBOUND);
        },
      };
    case INBOUND_ROUTES.APPROVAL:
      return {
        title: 'Inbound Approval',
        prevPageHandler: () => {
          navigate(INBOUND_ROUTES.APPROVAL_LIST);
        },
      };
    default:
      return { title: null, prevPageHandler: null };
  }
};

export const getHeaderConfig = (
  source: string,
  navigate: NavigateFunction,
  navigateParams?: Record<string, any>,
): IHeaderConfig => {
  switch (true) {
    case source.includes(DASHBOARD_ROUTE_ROOT):
      return _getDashboardHeaderConfig(source, navigate);
    case source.includes(HOME_ROUTE_ROOT):
      return _getHomeHeaderConfig(source, navigate);
    case source.includes(PICK_TASK_ROUTE_ROOT):
    case source.includes(PACK_TASK_ROUTE_ROOT):
      return _getPickAndPackHeaderConfig(source, navigate, navigateParams);
    case source.includes(REPLENISH_ROUTE_ROOT):
      return _getReplenishHeaderConfig(source, navigate);
    case source.includes(STOCK_TRANSFER_ROUTE_ROOT):
      return _getStockTransferHeaderConfig(source, navigate);
    case source.includes(INVENTORY_ADJUSTMENT_ROOT):
      return _getInventoryHeaderConfig(source, navigate);
    case source.includes(STO_PICK_ROUTE_ROOT):
      return _getSTOPickHeaderConfig(source, navigate);
    case source.includes(STO_CHECK_ROUTE_ROOT):
      return _getSTOCheckBasketHeaderConfig(source, navigate);
    case source.includes(STO_RECEIVE_ROUTE_ROOT):
      return _getSTOReceiveHeaderConfig(source, navigate);
    case source.includes(INBOUND_ROUTE_ROOT):
      return _getInboundHeaderConfig(source, navigate);
    case source.includes(DELIVERY_NOTE_ROUTE_ROOT):
      return _getDeliveryNoteHeaderConfig(source, navigate);
    default:
      return { title: null, prevPageHandler: null };
  }
};
